/**
 * The Sign queue (Treatment Plan spec v1.4, S-23, SG-010). Plans waiting for
 * this prescriber's signature, oldest first: patient, clinic, drafted by,
 * items and the banner state. Opening one opens the same Review and sign
 * drawer the builder uses (S-22). Mostly used by the collaborating physician,
 * Dr. Joe, for plans built by nurse accounts or for nurse-enrolled patients.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, EmptyState, ErrorState, InfoHint, LoadingRows } from './ui';
import { SignDrawer } from './SignDrawer';

export interface SignQueueRow {
  planId: string;
  patientId: string;
  version: number;
  patientName: string;
  clinicName: string | null;
  draftedBy: string;
  items: string[];
  bannerState: 'ready' | 'needs_you' | 'waiting';
  bannerLabel: string;
  sentToSignAt: string | null;
}

export const SIGN_QUEUE_KEY = ['sign-queue'] as const;

/** Visible only to prescribers; everyone else gets visible: false and no rows. */
export function useSignQueue() {
  return useQuery({
    queryKey: SIGN_QUEUE_KEY,
    queryFn: () => api.get<{ visible: boolean; rows: SignQueueRow[] }>('/signoff/queue'),
    refetchInterval: 60_000,
  });
}

const STATE_CHIP: Record<SignQueueRow['bannerState'], string> = {
  ready: 'bg-mint text-forest',
  needs_you: 'bg-risk-yellow-soft text-risk-yellow',
  waiting: 'bg-[#efeee9] text-[#4d6072]',
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

const waited = (iso: string | null) => {
  if (!iso) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}d`;
};

export function SignQueue({ showClinic = false }: { showClinic?: boolean }) {
  const qc = useQueryClient();
  const queue = useSignQueue();
  const [open, setOpen] = useState<SignQueueRow | null>(null);
  const [flash, setFlash] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  if (queue.isLoading) return <LoadingRows rows={4} />;
  if (queue.error) return <ErrorState error={queue.error} retry={() => void queue.refetch()} />;
  const rows = queue.data?.rows ?? [];

  return (
    <>
      {flash && (
        <p role="status" className={`mb-3 rounded-xl border px-4 py-3 text-[14px] ${flash.tone === 'ok' ? 'border-[#bcd5c9] bg-mint text-forest' : 'border-[#ecd9a0] bg-risk-yellow-soft text-risk-yellow'}`}>
          {flash.text}
        </p>
      )}
      {rows.length === 0 ? (
        <Card>
          <EmptyState title="Nothing waiting for your signature" body="When a nurse account sends a plan to you to sign, it waits here, oldest first." />
        </Card>
      ) : (
        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-hairline bg-[#f3f2ed] px-4 py-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy">Waiting for your signature, oldest first</span>
            <InfoHint label="About the Sign queue">
              Plans built by a nurse account, or for a patient a nurse enrolled, come to the collaborating physician to sign. The patient sees nothing until the plan is signed.
            </InfoHint>
          </div>
          <ul className="divide-y divide-hairline" data-testid="sign-queue">
            {rows.map((row) => (
              <li key={row.planId}>
                <button type="button" onClick={() => setOpen(row)} className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-mint/30">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#e3efe9] font-display text-[12px] font-semibold text-navy">{initials(row.patientName)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-[16px] font-semibold text-navy">{row.patientName}</span>
                    <span className="block truncate text-[13.5px] text-[#4d6072]">
                      {row.items.join(', ')}
                      {showClinic && row.clinicName ? ` · ${row.clinicName}` : ''} · drafted by {row.draftedBy}
                    </span>
                  </span>
                  <span className={`inline-flex shrink-0 items-center rounded-[5px] px-2 py-[3px] text-[11.5px] font-semibold ${STATE_CHIP[row.bannerState]}`}>{row.bannerLabel}</span>
                  {waited(row.sentToSignAt) && <span className="w-10 shrink-0 text-right text-[12.5px] text-[#5b6b7c]">{waited(row.sentToSignAt)}</span>}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {open && (
        <SignDrawer
          planId={open.planId}
          patientId={open.patientId}
          onClose={() => setOpen(null)}
          onSent={(text, tone) => {
            setOpen(null);
            setFlash({ text, tone });
            void qc.invalidateQueries({ queryKey: SIGN_QUEUE_KEY });
            void qc.invalidateQueries({ queryKey: ['nav-counts'] });
            void qc.invalidateQueries({ queryKey: ['sign-queue-count'] });
          }}
        />
      )}
    </>
  );
}
